import React, { useEffect, useState } from "react";
import {
  Button,
  Combobox,
  Input,
  InputBase,
  Text,
  Textarea,
  Tooltip,
  useCombobox,
} from "@mantine/core";
import { IconHelpCircle, IconSparkles } from "@tabler/icons-react";
import { aiEmailTooltips, tones } from "@/constants.ts";
import "./EmailGenerator.scss";
import { useEmailStore } from "@components/Tax Pro Only/Modals/EmailModal/emailStore.ts";
import { getSignatureName } from "@components/Tax Pro Only/Modals/EmailModal/utils.ts";

/**
 * EmailGenerator component generates an email based on the provided client name, tone, reason, and footer.
 * It integrates with the EmailContext to manage email generation states.
 *
 * @returns {JSX.Element} The EmailGenerator component.
 */
const EmailGenerator: React.FC = () => {
  const [reason, setReason] = useState("");
  const [footer, setFooter] = useState("");
  const [tone, setTone] = useState(tones[0]);
  const { businessInfo, isGenerating, generateEmail } = useEmailStore();

  useEffect(() => {
    if (businessInfo) {
      setFooter(`Best regards,\n${getSignatureName(businessInfo).trimStart()}`);
    }
  }, [businessInfo]);

  const onGenerateEmail = async () => {
    await generateEmail(reason, footer, tone);
  };

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const options = tones.map((item) => (
    <Combobox.Option value={item} key={item}>
      {item}
    </Combobox.Option>
  ));

  return (
    <div className="email-form">
      <div className="email-form-inputs">
        <Text c="#5A5A5A" className="email-form-input" size="sm">
          Use Jaz AI to generate email.
        </Text>
        <Textarea
          name="reason"
          label={
            <div className="form-input-label">
              <Text size="md" fw={500}>
                Reason for email
              </Text>
              <Tooltip label={aiEmailTooltips.reason}>
                <IconHelpCircle size={15} />
              </Tooltip>
            </div>
          }
          minRows={3}
          autosize
          placeholder="Example: A client is missing documents and I need them to send over the following: 1099-ER, 1098-T, and 1099-G"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          required
          data-testid="reason-for-email"
          classNames={{ label: "form-input-label" }}
          styles={{ required: { display: "none" } }}
        />

        <Textarea
          name="footer"
          label={
            <div className="form-input-label">
              <Text size="md" fw={500}>
                Footer
              </Text>
              <Tooltip label={aiEmailTooltips.footer}>
                <IconHelpCircle size={15} />
              </Tooltip>
            </div>
          }
          minRows={5}
          autosize
          placeholder="Email Footer"
          value={footer}
          onChange={(e) => setFooter(e.currentTarget.value)}
          required
          classNames={{ label: "form-input-label" }}
          styles={{ required: { display: "none" } }}
        />

        <Combobox store={combobox} onOptionSubmit={setTone}>
          <Combobox.Target>
            <InputBase
              label={
                <div className="form-input-label">
                  <Text size="md" fw={500}>
                    Choose a tone
                  </Text>
                  <Tooltip label={aiEmailTooltips.tone}>
                    <IconHelpCircle size={15} />
                  </Tooltip>
                </div>
              }
              component="button"
              defaultValue={tone}
              type="button"
              pointer
              rightSection={<Combobox.Chevron />}
              rightSectionPointerEvents="none"
              onClick={() => combobox.toggleDropdown()}
              required
              classNames={{ label: "form-input-label" }}
              styles={{ required: { display: "none" } }}
            >
              {tone || <Input.Placeholder>Pick value</Input.Placeholder>}
            </InputBase>
          </Combobox.Target>

          <Combobox.Dropdown>
            <Combobox.Options>{options}</Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>
      </div>

      <div className="email-form-button-container">
        <Button
          leftSection={<IconSparkles size={16} />}
          variant="gradient"
          gradient={{ from: "indigo", to: "cyan", deg: 104 }}
          radius="lg"
          w="100%"
          type="submit"
          disabled={isGenerating}
          onClick={onGenerateEmail}
        >
          Generate Email
        </Button>
      </div>
    </div>
  );
};

export default EmailGenerator;
